import {
  checkLevelSanity
} from './services/levelSanityChecker';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useMemo, useState } from 'react';
import SummaryCards from './components/SummaryCards';
import LevelTable from './components/LevelTable';
import { calculateRows } from './calculations/leveling';
import { downloadFile, toCsv } from './utils/export';
import { LevelRow } from './types/survey';
import OCRImport from './pages/OCRImport';

function blankRow(): LevelRow {
  return {
    id: Date.now().toString() + '-' + Math.random().toString(36).slice(2),
    chainage: '',
    offset: '',
    side: 'Center',
    bs: '',
    is: '',
    fs: '',
    hi: '',
    ch: '',
    offsetSigned: '',
    rl: '',
    tbmValue: '',
    remarks: ''
  };
}

function parseNumber(v: string) {
  if (!v || v.trim() === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmt(n: number) {
  return n.toFixed(3);
}

export default function App() {
  const [page, setPage] = useState<'manual' | 'ocr'>('manual');
  const [tbmText, setTbmText] = useState('');
  const [projectName, setProjectName] = useState('');
  const [bookDate, setBookDate] = useState(new Date().toISOString().slice(0, 10));
  const [startTbmName, setStartTbmName] = useState('');
  const [closingTbmName, setClosingTbmName] = useState('');
  const [closingTbmText, setClosingTbmText] = useState('');
   
  const [rows, setRows] = useState<LevelRow[]>([blankRow()]);
  const [ocrMode, setOcrMode] = useState<'levelbook' | 'ogl'>('levelbook');
  const tbm = parseNumber(tbmText);

  const calculatedRows = useMemo<LevelRow[]>(() => {
    const calculated = calculateRows(tbm, rows);

    let cpCount = 0;

    return calculated.map((row, index) => {
      const nextRow = calculated[index + 1];

      const hasFS =
        row.fs !== '' &&
        row.fs !== undefined &&
        row.fs !== null &&
        !isNaN(Number(row.fs));

      const nextHasBS =
        nextRow &&
        nextRow.bs !== '' &&
        nextRow.bs !== undefined &&
        nextRow.bs !== null &&
        !isNaN(Number(nextRow.bs));

      const hasBS =
        row.bs !== '' &&
        row.bs !== undefined &&
        row.bs !== null &&
        !isNaN(Number(row.bs));

     const closingRL = closingTbmText.trim()
        ? parseNumber(closingTbmText)
        : 0;

      const rowRL = row.rl ? parseNumber(row.rl) : 0;

      const isClosingTBM =
        closingTbmText.trim() !== '' &&
        row.fs?.trim() !== '' &&
        Math.abs(rowRL - closingRL) <= 0.005;

if (isClosingTBM) {
  return {
    ...row,
    remarks: 'CLOSING TBM',
  } as LevelRow;
}


      if (hasFS && (hasBS || nextHasBS)) {
        cpCount++;

return {
  ...row,
  remarks: `CP-${cpCount}` as LevelRow['remarks'],
} as LevelRow;
      }

      return row;

    });
  }, [tbm, rows, closingTbmText]);
  const sanityWarnings = useMemo(
  () => checkLevelSanity(calculatedRows),
  [calculatedRows]
);

  const totalBS = rows.reduce((s, r) => s + parseNumber(r.bs), 0);
  const totalFS = rows.reduce((s, r) => s + parseNumber(r.fs), 0);

  const lastFsRl = [...calculatedRows].reverse().find(r => r.fs && r.rl)?.rl || '';
  const closureError = closingTbmText.trim()
    ? parseNumber(lastFsRl) - parseNumber(closingTbmText)
    : 0;
const closureStatus =
  closingTbmText.trim() === ''
    ? 'PENDING'
    : Math.abs(closureError) <= 0.005
    ? 'ACCEPTED'
    : 'OUT OF TOLERANCE';

const setRow = (id: string, key: keyof LevelRow, value: string) => {
  setRows(prev => {
    const updated = prev.map(row =>
      row.id === id ? { ...row, [key]: value } : row
    );

    const editedIndex = updated.findIndex(row => row.id === id);
    const editedRow = updated[editedIndex];
    const isLastRow = editedIndex === updated.length - 1;

    const hasMeasurement =
      editedRow.bs?.trim() ||
      editedRow.is?.trim() ||
      editedRow.fs?.trim();

    const nextRow = updated[editedIndex + 1];

    const editedHasFS =
      editedRow.fs?.trim() !== '';

    const nextHasBS =
      nextRow &&
      nextRow.bs?.trim() !== '';
 
    const isCP =
      editedHasFS && nextHasBS;

    if (isCP) {
      updated.splice(editedIndex + 1, 0, blankRow());

      setTimeout(() => {
        const nextInput = document.querySelector(
          `[data-r="${editedIndex + 2}"][data-f="bs"]`
        ) as HTMLInputElement | null;

        nextInput?.focus();
      }, 100);

      return updated;
    }
        if (isLastRow && hasMeasurement) {
          return [...updated, blankRow()];
        }

    return updated;
   });
  };  

const addRow = () => setRows(prev => [...prev, blankRow()]);

  const clearRows = () => {
    localStorage.clear();
    setRows([blankRow()]);
    setTbmText('');
    setStartTbmName('');
    setClosingTbmName('');
    setClosingTbmText('');
  };

  const deleteRow = (id: string) => {
    setRows(prev => prev.filter(r => r.id !== id));
  };

  const exportCsv = () => {
    const header =
      `Starting TBM,${startTbmName}\n` +
      `Starting TBM RL,${tbmText}\n` +
      `Closing TBM,${closingTbmName}\n` +
      `Closing TBM RL,${closingTbmText}\n` +
      `Last FS RL,${lastFsRl}\n` +
      `Closing Error,${fmt(closureError)}\n\n`;

    downloadFile('level-sheet.csv', header + toCsv(calculatedRows), 'text/csv');
  };

  const exportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Level Book');
    sheet.headerFooter.oddFooter = '&CPage &P of &N';

    sheet.pageSetup = {
      paperSize: 9,
      orientation: 'landscape',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
    };

    sheet.mergeCells('A1:L1');
    sheet.getCell('A1').value = 'LEVEL BOOK';
    sheet.getCell('A1').font = { bold: true, size: 16 };
    sheet.getCell('A1').alignment = { horizontal: 'center' };

    sheet.mergeCells('A2:C2');
    sheet.getCell('A2').value = `Date: ${bookDate}`;

    sheet.mergeCells('J2:L2');
    sheet.getCell('J2').value = 'Page No.';
    sheet.headerFooter.oddFooter = '&RPage &P of &N';
    sheet.mergeCells('A3:C3');
    sheet.getCell('A3').value = `Starting TBM: ${startTbmName}`;

    sheet.mergeCells('D3:F3');
    sheet.getCell('D3').value = `Starting RL: ${tbmText}`;

    sheet.mergeCells('G3:I3');
    sheet.getCell('G3').value = `Closing TBM: ${closingTbmName}`;

    sheet.mergeCells('J3:L3');
    sheet.getCell('J3').value = `Closing RL: ${closingTbmText}`;

    sheet.mergeCells('A4:D4');
    sheet.getCell('A4').value = `Total BS: ${fmt(totalBS)}`;

    sheet.mergeCells('E4:H4');
    sheet.getCell('E4').value = `Total FS: ${fmt(totalFS)}`;

    sheet.mergeCells('I4:L4');
    sheet.getCell('I4').value = `Closing Error: ${fmt(closureError)}`;

    sheet.addRow([]);

    const header = ['Chainage', 'Offset', 'Side', 'BS', 'IS', 'FS', 'HI', 'CH', 'Offset(+/-)', 'RL', 'TBM Value', 'Remarks'];
    const headerRow = sheet.addRow(header);
    headerRow.font = { bold: true };

    calculatedRows.forEach(row => {
      sheet.addRow([
        row.chainage,
        row.offset,
        row.side,
        row.bs,
        row.is,
        row.fs,
        row.hi,
        row.ch,
        row.offsetSigned,
        row.rl,
        row.tbmValue,
        row.remarks,
      ]);
    });

    sheet.columns = [
      { width: 12 },
      { width: 10 },
      { width: 10 },
      { width: 10 },
      { width: 10 },
      { width: 10 },
      { width: 10 },
      { width: 10 },
      { width: 12 },
      { width: 10 },
      { width: 12 },
      { width: 12 },
    ];

    sheet.eachRow(row => {
      row.eachCell(cell => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
        cell.alignment = {
          horizontal: 'center',
          vertical: 'middle',
        };
      });
    });

  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    'level-book.xlsx'
  );
};
const exportPdf = async () => {
  const pdf = new jsPDF('p', 'mm', 'a4');

  pdf.setFontSize(16);
  pdf.text('LEVEL BOOK', 105, 12, { align: 'center' });

  pdf.setFontSize(9);
  pdf.text(`Project: ${projectName || ''}`, 10, 22);
  pdf.text(`Date: ${bookDate}`, 150, 22);

  pdf.text(`Starting TBM: ${startTbmName}`, 10, 28);
  pdf.text(`Starting RL: ${tbmText}`, 70, 28);
  pdf.text(`Closing TBM: ${closingTbmName}`, 10, 34);
  pdf.text(`Closing RL: ${closingTbmText}`, 70, 34);

  autoTable(pdf, {
    startY: 42,
    head: [[
      'Chainage', 'Offset', 'Side', 'BS', 'IS', 'FS',
      'HI', 'CH', 'Offset(+/-)', 'RL', 'TBM Value', 'Remarks'
    ]],
    body: calculatedRows.map(row => [
      row.chainage,
      row.offset,
      row.side,
      row.bs,
      row.is,
      row.fs,
      row.hi,
      row.ch,
      row.offsetSigned,
      row.rl,
      row.tbmValue,
      row.remarks,
    ]),
    theme: 'grid',
    styles: {
      fontSize: 6,
      cellPadding: 1,
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      fontStyle: 'bold',
    },
    didParseCell: data => {

const raw = data.row.raw as string[];
const remarks = String(raw?.[11] ?? '');
      if (remarks.startsWith('CP-')) {
        data.cell.styles.fillColor = [255, 245, 157];
        data.cell.styles.fontStyle = 'bold';
      }
if (remarks === 'TBM') {
  data.cell.styles.fillColor = [200, 247, 197];
  data.cell.styles.fontStyle = 'bold';
}
    },
  });

  pdf.setFontSize(9);
  pdf.text(`Total BS: ${fmt(totalBS)}`, 10, 285);
  pdf.text(`Total FS: ${fmt(totalFS)}`, 55, 285);
  pdf.text(`Closure Error: ${fmt(closureError)}`, 100, 285);
  pdf.text(`Closure Status: ${closureStatus}`, 150, 285);

  pdf.save('level-book.pdf');
};

  return (
    <div className="app">
  <div style={{ marginBottom: '20px' }}>
  <button onClick={() => setPage('manual')}>
    Manual Entry
  </button>

  <button
    style={{ marginLeft: '10px' }}
    onClick={() => setPage('ocr')}
  >
    OCR Import
  </button>
</div>   
{
page === 'ocr'
  ? (
      <>
        <div style={{ marginBottom: '15px' }}>
          <label>
            OCR Mode:&nbsp;
            <select
              value={ocrMode}
              onChange={(e) =>
                setOcrMode(e.target.value as 'levelbook' | 'ogl')
              }
            >
              <option value="levelbook">Level Book OCR</option>
              <option value="ogl">Cross Section / OGL OCR</option>
            </select>
          </label>
        </div>

        <OCRImport
          mode={ocrMode}
          onImportRows={(ocrRows) => {
            setRows(ocrRows);
            setPage('manual');
          }}
        />
      </>
    )
    : (
      <div className="panel">
       <h1>{projectName || 'LEVEL BOOK'}</h1>
        <p>TBM, BS, IS, FS, HI, RL, Chainage, Offset, LHS/RHS handling and CSV export.</p>

        <div className="tbm-grid">
        <label>
          Project Name / Board
          <input
            value={projectName}
            onChange={e => setProjectName(e.target.value)}
            placeholder="Enter Project Name"
          />
        </label>

        <label>
          Date
          <input
            type="date"
            value={bookDate}
            onChange={e => setBookDate(e.target.value)}
          />
        </label>  
        <label>
            Starting TBM Name/No
            <input value={startTbmName} onChange={e => setStartTbmName(e.target.value)} />
          </label>

          <label>
            Starting TBM RL
            <input value={tbmText} onChange={e => setTbmText(e.target.value)} />
          </label>

          <label>
            Closing TBM Name/No
            <input value={closingTbmName} onChange={e => setClosingTbmName(e.target.value)} />
          </label>

          <label>
            Closing TBM RL
            <input value={closingTbmText} onChange={e => setClosingTbmText(e.target.value)} />
          </label>
        </div>

        <SummaryCards
          rows={rows.length}
          totalBS={fmt(totalBS)}
          totalFS={fmt(totalFS)}
          closureError={fmt(closureError)}
          closureStatus={closureStatus}
        />

        <div className="buttons">
          <button onClick={addRow}>Add row</button>
          <button onClick={clearRows}>Clear</button>
          <button onClick={exportPdf}>PDF export</button>
          <button onClick={exportExcel}>Excel export</button>
          <button onClick={() => window.print()}>
            Print Level Book
          </button>
        </div>
        <div className="print-title">
          LEVEL BOOK
        </div>
        <LevelTable rows={calculatedRows} setRow={setRow} deleteRow={deleteRow} />
       {sanityWarnings.length > 0 && (
  <div
    style={{
      marginTop: '20px',
      padding: '10px',
      border: '1px solid red',
      borderRadius: '8px'
    }}
  >
    <h3>⚠ Level Book Warnings</h3>

    {sanityWarnings.map((w, i) => (
      <div key={i}>
        Row {w.rowIndex}: {w.message}
      </div>
    ))}
  </div>
)} 
     </div>
    )}
  </div>
);
}
